import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { hasInlineGeo, temporalProperties, titleCase } from '@/features/schema/format'
import type { PropertySchema } from '@/services/api/schemas'
import { cn } from '@/utils/cn'
import { PHONE_TAP_HALO } from './phone-classes'

export interface ConventionsEditorProps {
  kind: 'entity' | 'relation'
  properties: PropertySchema[]
  trackable?: boolean
  onTrackableChange?: (value: boolean) => void
  timelineExclude: string[]
  onTimelineExcludeChange: (value: string[]) => void
  displayLabel?: Record<string, string>
  onDisplayLabelChange?: (value: Record<string, string>) => void
}

/**
 * Renders the small set of per-entity / per-relation convention override fields
 * that the temporal-geo conventions module reads. See /docs/schema-conventions
 * for the full contract. Pure controlled component — emits change callbacks; it
 * performs no IO.
 */
export function ConventionsEditor({
  kind,
  properties,
  trackable,
  onTrackableChange,
  timelineExclude,
  onTimelineExcludeChange,
  displayLabel,
  onDisplayLabelChange,
}: ConventionsEditorProps) {
  const { t } = useTranslation()
  const temporalProps = temporalProperties(properties)
  const trackableLocked = hasInlineGeo(properties)

  const toggleExcluded = (name: string) => {
    if (timelineExclude.includes(name)) {
      onTimelineExcludeChange(timelineExclude.filter(n => n !== name))
    } else {
      onTimelineExcludeChange([...timelineExclude, name])
    }
  }

  const setLabelFor = (name: string, value: string) => {
    if (!onDisplayLabelChange || !displayLabel) return
    const next = { ...displayLabel }
    if (value.trim() === '') {
      delete next[name]
    } else {
      next[name] = value
    }
    onDisplayLabelChange(next)
  }

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm text-foreground">
          {t('schemaAgent.editor.conventions.title')}
        </Label>
        <a
          href="/docs/schema-conventions"
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'text-xs text-muted-foreground underline hover:text-foreground',
            PHONE_TAP_HALO
          )}
        >
          {t('schemaAgent.editor.conventions.reference')}
        </a>
      </div>

      {kind === 'entity' && onTrackableChange && (
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-0.5">
            <Label className="text-sm text-foreground">
              {t('schemaAgent.editor.conventions.trackable')}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t('schemaAgent.editor.conventions.trackableHelp')}
            </p>
            {trackableLocked && (
              <p className="text-xs text-warning mt-1">
                {t('schemaAgent.editor.conventions.trackableLocked')}
              </p>
            )}
          </div>
          <Switch
            checked={!!trackable && !trackableLocked}
            onCheckedChange={onTrackableChange}
            disabled={trackableLocked}
          />
        </div>
      )}

      {temporalProps.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-sm text-foreground">
            {t('schemaAgent.editor.conventions.timelineExcluded')}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t('schemaAgent.editor.conventions.timelineExcludedHelp')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {temporalProps.map(p => {
              const excluded = timelineExclude.includes(p.name)
              return (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => toggleExcluded(p.name)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md border px-2 py-1.5 md:py-0.5 text-xs font-mono transition',
                    excluded
                      ? 'border-warning-border bg-warning-surface text-warning-foreground line-through'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                  )}
                  title={t(`schemaAgent.editor.conventions.${excluded ? 'excluded' : 'included'}`)}
                >
                  {p.name}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {kind === 'entity' && temporalProps.length > 0 && onDisplayLabelChange && displayLabel && (
        <div className="space-y-1.5">
          <Label className="text-sm text-foreground">
            {t('schemaAgent.editor.conventions.displayLabels')}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t('schemaAgent.editor.conventions.displayLabelsHelp')}
          </p>
          <div className="space-y-1.5">
            {temporalProps.map(p => (
              <div key={p.name} className="flex items-center gap-2">
                <code className="text-xs text-info-foreground w-32 truncate" title={p.name}>
                  {p.name}
                </code>
                <Input
                  value={displayLabel[p.name] ?? ''}
                  placeholder={titleCase(p.name)}
                  onChange={e => setLabelFor(p.name, e.target.value)}
                  className="bg-card border-border h-8 text-xs"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {temporalProps.length === 0 && (
        <p className="text-xs text-muted-foreground italic">
          {t('schemaAgent.editor.conventions.noTemporal')}
        </p>
      )}
    </div>
  )
}
